import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate, parseDayKey, toDayKey } from '@/lib/clinic/datetime'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface DateFieldProps {
    id?: string
    value: string | null
    onChange: (value: string) => void
    placeholder?: string
    invalid?: boolean
    yearRange?: [number, number]
    className?: string
    ariaLabel?: string
}

const DateField: FC<DateFieldProps> = ({
    id,
    value,
    onChange,
    placeholder = 'DD-MM-YYYY',
    invalid = false,
    yearRange,
    className,
    ariaLabel
}): ReactNode => {
    const [open, setOpen] = useState(false)
    const selected = value ? parseDayKey(value) : undefined

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                render={
                    <Button
                        id={id}
                        type='button'
                        variant='outline'
                        aria-invalid={invalid || undefined}
                        aria-label={ariaLabel}
                        className={cn('w-full justify-start gap-2 px-3 font-normal tabular-nums', className)}
                    />
                }
            >
                <CalendarDays className='size-4 text-muted-foreground' />

                {value ? (
                    <span>
                        {formatDate(value)}
                    </span>
                ) : (
                    <span className='text-muted-foreground'>
                        {placeholder}
                    </span>
                )}
            </PopoverTrigger>

            <PopoverContent align='start' className='w-auto p-0'>
                <Calendar
                    mode='single'
                    selected={selected}
                    defaultMonth={selected}
                    captionLayout={yearRange ? 'dropdown' : 'label'}
                    startMonth={yearRange ? new Date(yearRange[0], 0) : undefined}
                    endMonth={yearRange ? new Date(yearRange[1], 11) : undefined}
                    onSelect={date => {
                        if (!date) return

                        onChange(toDayKey(date))
                        setOpen(false)
                    }}
                />
            </PopoverContent>
        </Popover>
    )
}

export default DateField
